import { PlusIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Badge, Box, Code, Flex, Grid, Link } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { ReassignListsDialog } from 'components/common/pitch-lists/reassign-lists';
import { CommonTable } from 'components/common/table';
import { CommonTableButton } from 'components/common/table/button';
import { TableProvider } from 'components/common/table/context';
import { RestoreListsDialog } from 'components/sections/admin-portal/dialogs/restore-lists';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { DeleteUsersDialog } from 'components/sections/admin-portal/users/dialogs/delete-users';
import { EditUserDialog } from 'components/sections/admin-portal/users/dialogs/edit-user';
import { ViewSessionsDialog } from 'components/sections/admin-portal/users/dialogs/view-sessions';
import { TeamsContext } from 'contexts/admin/teams.context';
import { UsersContext } from 'contexts/admin/users.context';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import lightFormat from 'date-fns/lightFormat';
import parseISO from 'date-fns/parseISO';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { USER_ROLES, UserStatus } from 'lib_ts/enums/auth.enums';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IUser } from 'lib_ts/interfaces/i-user';
import { IPitchListPutManyRequest } from 'lib_ts/interfaces/pitches/i-pitch-list';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { AdminUsersService } from 'services/admin/users.service';
import { useAuthStore } from 'stores/use-auth-store';
import useMiscStore from 'stores/use-misc-store';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

enum DialogMode {
  Delete,
  Editor,
  Reassign,
  Restore,
  ResetPassword,
  Sessions,
}

export const UsersTable = () => {
  const navigate = useNavigate();
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const machines = useMachinesPortalStore((s) => s.machines);

  const { options: teamOptions } = useContext(TeamsContext);
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const {
    filter,
    filtered,
    loading: usersLoading,
    options,
    mergeFilter,
    refresh,
    updateUser,
  } = useContext(UsersContext);

  const machineOptions = useMemo(
    () =>
      machines.map((o) => ({
        label: MachineContextHelper.getMachineMenuLabel(o),
        value: o.machineID,
      })),
    [machines]
  );

  const [manageUser, setManageUser] = useState<IUser>();
  const [reassignListsPayload, setReassignListsPayload] =
    useState<IPitchListPutManyRequest>({ filter: {}, update: {} });
  const [deleteUsers, setDeleteUsers] = useState<IUser[]>();

  const [dialog, setDialog] = useState<{ mode: DialogMode; key: number }>();

  const columns = useMemo<ITypedTableColumn<IUser>[]>(
    () => [
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        actions: [
          {
            label: 'common.edit',
            onClick: (user) => {
              setManageUser(user);
              setDialog({ mode: DialogMode.Editor, key: Date.now() });
            },
          },
          {
            label: 'admin.reset-password',
            color: RADIX.COLOR.WARNING,
            onClick: (user) => {
              setManageUser(user);
              setDialog({ mode: DialogMode.ResetPassword, key: Date.now() });
            },
          },
          {
            label: 'admin.restore-lists',
            invisibleFn: () => !userRoleFeatureFlags.adminUsers.restoreLists,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.WARNING,
            onClick: (user) => {
              setManageUser(user);
              setDialog({ mode: DialogMode.Restore, key: Date.now() });
            },
          },
          {
            label: 'common.reassign-lists',
            onClick: (user) => {
              const payload: IPitchListPutManyRequest = {
                filter: {
                  _parent_id: user._id,
                  _parent_def: PitchListOwner.User,
                },
                update: {
                  processed: new Date(),
                  process_notes: `reassigned from user ${user.email} by admin ${current.email}`,
                  _parent_def: PitchListOwner.User,
                },
              };

              setReassignListsPayload(payload);
              setDialog({ mode: DialogMode.Reassign, key: Date.now() });
            },
            color: RADIX.COLOR.WARNING,
          },
          {
            label: 'admin.view-sessions',
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: () => !userRoleFeatureFlags.adminUsers.viewSessions,
            onClick: (user) => {
              setManageUser(user);
              setDialog({ mode: DialogMode.Sessions, key: Date.now() });
            },
            color: RADIX.COLOR.WARNING,
          },
          {
            label: 'admin.impersonate',
            suffixIcon: <SuperAdminIcon />,
            invisibleFn: (user) =>
              !userRoleFeatureFlags.impersonation ||
              user.status !== UserStatus.verified,
            onClick: async (user) => {
              const ok = await useAuthStore
                .getState()
                .impersonate({ email: user.email });
              if (!ok) {
                return;
              }
              navigate(paths.home);
            },
            color: RADIX.COLOR.WARNING,
          },
          {
            label: 'admin.toggle-access',
            onClick: async (user) => {
              const wasVerified = user.status === UserStatus.verified;

              const success = await updateUser(
                {
                  _id: user._id,
                  status: wasVerified
                    ? UserStatus.deactivated
                    : UserStatus.verified,
                },
                true
              );

              if (!success) {
                NotifyHelper.error({
                  message_md: t('common.request-failed-msg'),
                });

                return;
              }

              NotifyHelper.success({
                message_md: `User ${user.email} has been ${
                  wasVerified ? 'deactivated' : 'activated'
                }!`,
              });
            },
            color: RADIX.COLOR.DANGER,
          },
          {
            label: 'common.delete',
            suffixIcon: <SuperAdminIcon />,
            onClick: (user) => {
              setDeleteUsers([user]);
              setDialog({ mode: DialogMode.Delete, key: Date.now() });
            },
            color: RADIX.COLOR.DANGER,
            invisibleFn: () => !userRoleFeatureFlags.adminUsers.delete,
          },
        ],
      },
      {
        label: 'common.created',
        key: '_created',
        formatFn: (m) => lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT),
      },
      {
        label: 'common.email',
        key: 'email',
        formatFn: (m) => <Link href={`mailto:${m.email}`}>{m.email}</Link>,
      },
      {
        label: 'common.machine',
        key: 'machineID',
        formatFn: (m) => <Badge>{m.machineID}</Badge>,
      },
      {
        label: 'admin.role',
        key: 'role',
        formatFn: (m) => {
          const label = USER_ROLES.find((r) => r.value === m.role)?.label;
          return label ? (
            t(label)
          ) : (
            <Badge color={RADIX.COLOR.DANGER}> {m.role}</Badge>
          );
        },
      },
      {
        label: 'admin.access',
        key: '_access',
        formatFn: (v) => (
          <Badge
            color={
              v.status === UserStatus.verified
                ? RADIX.COLOR.SUCCESS
                : RADIX.COLOR.DANGER
            }
          >
            {v.status === UserStatus.verified ? 'Enabled' : 'Disabled'}
          </Badge>
        ),
        sortRowsFn: (a, b, dir: number) =>
          -StringHelper.safeSort(a.status, b.status) * dir,
      },
    ],
    [current.email, navigate, userRoleFeatureFlags]
  );

  const checkedActions = useCallback(
    (checked: IUser[]): IMenuAction[] => {
      const actions: IMenuAction[] = [
        {
          label: 'admin.copy-emails',
          onClick: () => {
            navigator.clipboard
              .writeText(checked.map((u) => u.email).join(';'))
              .then(() =>
                NotifyHelper.success({
                  message_md: 'Copied user emails to clipboard!',
                })
              );
          },
        },
        {
          label: 'common.delete',
          suffixIcon: <SuperAdminIcon />,
          color: RADIX.COLOR.DANGER,
          invisible: !userRoleFeatureFlags.adminUsers.delete,
          onClick: () => {
            setDeleteUsers(checked);
            setDialog({ mode: DialogMode.Delete, key: Date.now() });
          },
        },
      ];

      return actions;
    },
    [userRoleFeatureFlags]
  );

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminUserList,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'email',
      dir: -1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: checkedActions,
  };

  return (
    <ErrorBoundary identifier="APUsers">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <TableProvider identifier="APUsersTableProvider">
            <CheckedProvider data={filtered}>
              <CheckedContext.Consumer>
                {(checkedCx) => (
                  <CommonTable
                    identifier="APUsersTable"
                    dataTestID="APUsers"
                    toolbarContent={
                      <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
                        <Grid columns="4" gap={RADIX.FLEX.GAP.SM} flexGrow="1">
                          <Box>
                            <CommonSearchInput
                              id="users-email"
                              placeholder="common.email"
                              options={options.emails}
                              values={filter.email}
                              onChange={(v) => {
                                checkedCx.resetChecked();
                                mergeFilter({ email: v });
                              }}
                              multiple
                              optional
                            />
                          </Box>
                          <Box>
                            <CommonSearchInput
                              id="users-team"
                              placeholder="common.team"
                              options={teamOptions.teams}
                              values={filter.team}
                              onChange={(v) => {
                                checkedCx.resetChecked();
                                mergeFilter({ team: v });
                              }}
                              multiple
                              optional
                            />
                          </Box>
                          <Box>
                            <CommonSearchInput
                              id="users-machine"
                              placeholder="common.machine"
                              options={machineOptions}
                              values={filter.machines}
                              onChange={(v) => {
                                checkedCx.resetChecked();
                                mergeFilter({ machines: v });
                              }}
                              multiple
                              optional
                            />
                          </Box>
                          <Box>
                            <CommonSearchInput
                              id="users-created"
                              placeholder="common.date-added"
                              options={options._created.map((o) => ({
                                label: o,
                                value: o,
                              }))}
                              values={filter.created}
                              onChange={(v) => {
                                checkedCx.resetChecked();
                                mergeFilter({ created: v });
                              }}
                              multiple
                              optional
                              reverseSort
                            />
                          </Box>
                        </Grid>
                        <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
                          <CommonTableButton
                            label="common.refresh"
                            icon={<UpdateIcon />}
                            color={RADIX.COLOR.NEUTRAL}
                            disabled={usersLoading}
                            variant="soft"
                            onClick={() => {
                              checkedCx.resetChecked();
                              refresh();
                            }}
                          />

                          {userRoleFeatureFlags.adminUsers.create && (
                            <CommonTableButton
                              color={RADIX.COLOR.SUCCESS}
                              disabled={usersLoading}
                              onClick={() => {
                                setManageUser(undefined);
                                setDialog({
                                  mode: DialogMode.Editor,
                                  key: Date.now(),
                                });
                              }}
                              icon={<PlusIcon />}
                              label="common.create"
                            />
                          )}
                        </Flex>
                      </Flex>
                    }
                    displayColumns={columns}
                    displayData={filtered}
                    enableListener={!anyOpenDialogs}
                    {...pagination}
                    {...checkable}
                    {...select}
                    {...sort}
                    vFlex
                  />
                )}
              </CheckedContext.Consumer>
            </CheckedProvider>
          </TableProvider>
        }
      />

      {dialog?.mode === DialogMode.ResetPassword && manageUser && (
        <CommonConfirmationDialog
          identifier="ResetPassword"
          title="admin.reset-password"
          content={
            <p>
              Are you sure you want to reset <Code>{manageUser.email}</Code>'s
              password?
            </p>
          }
          action={{
            label: 'admin.reset-password',
            onClick: async () => {
              if (!manageUser) {
                setDialog(undefined);
                return;
              }

              const result =
                await AdminUsersService.getInstance().resetPasswordUrl(
                  manageUser._id
                );

              if (!result.success) {
                NotifyHelper.warning({
                  message_md:
                    result.error ??
                    `There was an error generating a reset password URL for ${
                      manageUser?.email ?? '(no email)'
                    }.`,
                });
                return;
              }

              const url = result.data as string;

              NotifyHelper.info({
                message_md:
                  'Password reset URL generated. How would you like to notify the user?',
                delay_ms: 0,
                buttons: [
                  {
                    label: 'Link',
                    dismissAfterClick: true,
                    onClick: () => {
                      navigator.clipboard.writeText(url).then(() =>
                        NotifyHelper.success({
                          message_md: 'Copied URL to clipboard.',
                        })
                      );
                    },
                  },
                  {
                    label: 'Email',
                    dismissAfterClick: true,
                    onClick: () => {
                      NotifyHelper.info({
                        message_md: 'Attempting to open email client...',
                      });

                      /** this will allow the mailto link to preserve the ?email and &code parts of the URL */
                      const escapedUrl = url
                        .replace('?', '%3F')
                        .replace('&', '%26');
                      window.open(
                        `mailto:${manageUser?.email}?subject=Trajekt App Password Reset&body=Please visit the following link to  reset your password:%0D%0A%0D%0A${escapedUrl}`
                      );
                    },
                  },
                ],
              });

              setDialog(undefined);
            },
          }}
          cancel={{
            onClick: () => {
              setDialog(undefined);
            },
          }}
        />
      )}
      {dialog?.mode === DialogMode.Sessions && manageUser && (
        <ViewSessionsDialog
          key={dialog.key}
          user={manageUser}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.mode === DialogMode.Restore && manageUser && (
        <RestoreListsDialog
          key={dialog.key}
          name={manageUser.email}
          parent_def={PitchListOwner.User}
          parent_id={manageUser._id}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.mode === DialogMode.Reassign && (
        <ReassignListsDialog
          key={dialog.key}
          identifier="UsersTableReassignListsDialog"
          refPayload={reassignListsPayload}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.mode === DialogMode.Editor && (
        <EditUserDialog
          key={dialog.key}
          id={manageUser?._id}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.mode === DialogMode.Delete && deleteUsers && (
        <DeleteUsersDialog key={dialog.key} users={deleteUsers} />
      )}
    </ErrorBoundary>
  );
};
